import React, { useState } from 'react'
import { useTranslation } from 'next-i18next'
import { makeStaticProps } from '../lib/getStatic'
import { NextSeo } from 'next-seo'
import { HeroIcons } from '../lib/utils/heroIcons'
import hubspotService from '../services/hubspot.service'
import { Language } from '../lib/types'
import gtmService from '../services/gtm.service'
import useLocale from '../hooks/useLocale'
import classNames from 'classnames'

export default function NewsletterPage() {
  const { t } = useTranslation('newsletter')
  const { locale } = useLocale()

  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleNewsletterSubmit = async () => {
    if (!email) return

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('form.results.errors.invalidEmail'))
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      await hubspotService.newsletterSubscription({
        email,
        language: locale as Language,
        note: 'Submitted via newsletter subscription page',
      })

      setSubmitted(true)

      const emailDomain = email.split('@')[1]
      gtmService.newsletterSubmitted({
        emailDomain,
        clickSource: 'newsletter-subscribe-page',
      })
    } catch (err) {
      setError(t('form.results.errors.submitEmailError'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <NextSeo
        title={t('seo.title') || ''}
        description={t('seo.description') || ''}
      />
      <div
        data-navtheme="frosted"
        className="h-auto min-h-screen w-full bg-dark-desktop-ornament-min bg-cover sm:h-screen"
      >
        <div className="relative mx-auto h-full w-full px-3 md:px-10 lg:px-16 xl:px-24 2xl:px-44">
          <div className="flex h-full w-full items-center justify-center">
            <div className="relative py-16 sm:py-0">
              <img
                className="absolute -right-6 -top-48 hidden h-60 origin-bottom animate-riseFromHorizon sm:block"
                src="/img/icons/thiings/mailbox.png"
                alt="White Shelters logo"
                loading={'lazy'}
              />

              <img
                className="absolute -left-24 -top-32 hidden h-48 -rotate-6 transition hover:scale-105 sm:block"
                src="/img/icons/thiings/new-mail.png"
                alt="White Shelters logo"
                loading={'lazy'}
              />
              <div className="relative z-20 rounded-lg bg-white p-1.5">
                <div className="relative isolate overflow-hidden rounded-md bg-simple-gradient py-10 shadow-md shadow-white sm:py-24 lg:py-32">
                  <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-2">
                      <div className="max-w-xl lg:max-w-lg">
                        <h1 className="text-4xl font-semibold tracking-tight text-white">
                          {t('title')}
                        </h1>
                        <p className="mt-4 text-lg text-gray-100">
                          {t('subtitle')}
                        </p>
                        <div className="mt-6 flex max-w-md flex-col gap-x-4 space-y-3 sm:flex-row sm:space-y-0">
                          <div className="relative w-full flex-auto">
                            <img
                              className="absolute -right-6 -top-[8vh] block h-20 origin-bottom animate-riseFromHorizon sm:hidden"
                              src="/img/icons/thiings/mailbox.png"
                              alt="White Shelters logo"
                              loading={'lazy'}
                            />
                            <input
                              id="email-address"
                              name="email"
                              type="email"
                              onChange={(e) => setEmail(e.target.value)}
                              required
                              placeholder={t('form.placeholder')}
                              autoComplete="email"
                              className="relative w-full min-w-0 flex-auto rounded-md bg-white px-3.5 py-2 text-base text-black outline-1 -outline-offset-1 outline-white/10 placeholder:text-gray-700 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-500 sm:text-sm/6"
                            />
                          </div>
                          <button
                            type="button"
                            disabled={submitting || submitted}
                            onClick={handleNewsletterSubmit}
                            className={classNames(
                              'shadow-xs flex-none rounded-md px-3.5 py-2.5 text-sm font-semibold text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500',
                              submitted
                                ? 'bg-green-600'
                                : 'bg-gradient hover:bg-indigo-400',
                            )}
                          >
                            <div className="flex w-full items-center justify-center">
                              {submitting ? (
                                <>
                                  <>{t('form.button.submitting')}</>
                                  <HeroIcons.ArrowPathIcon className="ml-2 h-auto w-5 animate-spin" />
                                </>
                              ) : (
                                <>
                                  {!submitted ? (
                                    t('form.button.subscribe')
                                  ) : (
                                    <>
                                      <>{t('form.button.subscribed')}</>
                                      <HeroIcons.CheckIcon className="ml-2 h-auto w-5" />
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          </button>
                        </div>
                        {error && (
                          <p className="mt-1 text-xs text-red-500">{error}</p>
                        )}
                      </div>
                      <dl className="grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 lg:pt-2">
                        <div className="flex flex-col items-start">
                          <div className="rounded-md bg-white p-3">
                            <HeroIcons.CalendarDaysIcon
                              aria-hidden="true"
                              className="size-6 text-sheltersBlue"
                            />
                          </div>
                          <dt className="mt-4 text-base font-semibold text-white">
                            {t('sideText.monthlyArticles.title')}
                          </dt>
                          <dd className="mt-2 text-base/7 text-gray-300">
                            {t('sideText.monthlyArticles.text')}
                          </dd>
                        </div>
                        <div className="flex flex-col items-start">
                          <div className="rounded-md bg-white p-3">
                            <HeroIcons.HandRaisedIcon
                              aria-hidden="true"
                              className="size-6 text-sheltersBlue"
                            />
                          </div>
                          <dt className="mt-4 text-base font-semibold text-white">
                            {t('sideText.noSpam.title')}
                          </dt>
                          <dd className="mt-2 text-base/7 text-gray-300">
                            {t('sideText.noSpam.text')}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const getStaticProps = makeStaticProps([
  'common',
  'index',
  'newsletter',
  'navbar',
  'footer',
  'modals',
])

export { getStaticProps, NewsletterPage }
